import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  MarketPriceEstimationData,
  AppraisalPointData,
  NumberOfFailedBidData,
  OldAgeData,
  SalesDaysData,
} from '../../../interface/aiAnalyze/aiRecommend/data';
import { MyPatternListProps } from '../../../interface/aiAnalyze/aiRecommend/type';
import QuotaService from '../../../service/quota/Quota';
import { RegionProps, SidoList } from '../../search/filter/SampleData';
import { isFinalMonth } from '../../../utils/auth';
import { useUser } from '../../../context/UserContext';
import { useModal } from '../../../context/ModalContext';
import BottomModal from '../../components/modal/BottomModal';
import { getInvestChips, getProfitChips } from '../myAlgorithm/formatOptions';
import RegionService from '../../../service/Region';

interface Props {
  setIsModalOpen: (value: boolean) => void;
  data: MyPatternListProps[];
  selectedItem: MyPatternListProps;
  setSelectedItem: (value: MyPatternListProps) => void;
  setIsRecommend: (i: boolean) => void;
}

const PatternListModal = ({
  setIsModalOpen,
  data,
  selectedItem,
  setSelectedItem,
  setIsRecommend,
}: Props) => {
  const { user } = useUser();
  const { modalDispatch } = useModal();

  const [checkedItem, setCheckedItem] = useState(data[0]);
  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);

  useEffect(() => {
    const fetchRegionData = async () => {
      if (!data[0]?.pnu) return;

      const sidoCode = data[0].pnu.substring(0, 2);
      const sigunguCode = data[0].pnu.substring(0, 5);

      try {
        const sigunguRes = await RegionService.list(sidoCode);
        setSigunguList(sigunguRes.data);

        const emdRes = await RegionService.list(sigunguCode);
        setEmdList(emdRes.data);
      } catch (err) {
        console.error('지역 정보 불러오기 실패:', err);
      }
    };

    fetchRegionData();
  }, [data]);

  useEffect(() => {
    if (selectedItem.algorithm_idx !== 0) {
      setCheckedItem(selectedItem);
    }
  }, [selectedItem]);

  const navigate = useNavigate();

  // 서비스 이용권 확인
  const getServiceQuotaCheck = async (p_idx: number) => {
    try {
      const res = await QuotaService.check('E', undefined, undefined, p_idx);
      return res.data;
    } catch (err) {
      console.log(err, '발생');
    }
  };

  const handleConfirmButton = async () => {
    if (!checkedItem.regionAccess) {
      modalDispatch({
        type: 'USER_ALERT',
        payload: 'local',
      });
      return;
    }
    const quotaAccess = await getServiceQuotaCheck(checkedItem.algorithm_idx);
    if (!quotaAccess) {
      if (isFinalMonth(user?.plan_end)) {
        modalDispatch({
          type: 'USER_ALERT',
          payload: 'quotaDepletedExpiring',
        });
      } else {
        modalDispatch({
          type: 'USER_ALERT',
          payload: 'quotaDepleted',
        });
      }
      return;
    }
    setSelectedItem(checkedItem);
    setIsRecommend(false);
    setIsModalOpen(false);
  };
  return (
    <BottomModal onClose={() => setIsModalOpen(false)}>
      <Title>
        <Title_Text>내 알고리즘 목록</Title_Text>
        <img
          src="/assets/images/close/close-black-14.svg"
          alt=""
          onClick={() => setIsModalOpen(false)}
        />
      </Title>
      <Inner>
        <Content>
          <ViewAllButtonWrapper>
            <ViewAllButton onClick={() => navigate('/myAlgorithmList')}>
              전체보기 {'>'}
            </ViewAllButton>
          </ViewAllButtonWrapper>
          <ListBox>
            {data.map((item) => (
              <ListItem
                key={item.algorithm_idx}
                onClick={() => setCheckedItem(item)}
              >
                <CheckBoxContainer>
                  <img
                    src={`/assets/images/check/circle-checked-${checkedItem.algorithm_idx === item.algorithm_idx ? 'blue' : 'gray'}.svg`}
                    alt=""
                  />
                </CheckBoxContainer>
                <PatternTextBox>
                  <PatternName>
                    <span>
                      {item.name && item.name.trim() !== ''
                        ? item.name
                        : `나만의 알고리즘${item.algorithm_idx ? `${item.algorithm_idx}` : ''}`}
                    </span>
                  </PatternName>
                  <Patterns>
                    {getInvestChips(item, sigunguList, emdList).map((i) => (
                      <span key={i.key}>
                        {i.label} {i.value}
                        &nbsp;
                      </span>
                    ))}
                    {getProfitChips(item).map((i) => (
                      <span key={i.key}>
                        {i.label} {i.value}
                        &nbsp;
                      </span>
                    ))}
                  </Patterns>
                </PatternTextBox>
              </ListItem>
            ))}
          </ListBox>
        </Content>
      </Inner>
      <ModalButtonBox>
        <CancelButton
          onClick={() => {
            setIsModalOpen(false);
          }}
        >
          취소
        </CancelButton>
        <AnalyzeButton style={{ flex: 1 }} onClick={handleConfirmButton}>
          알고리즘 적용
        </AnalyzeButton>
      </ModalButtonBox>
    </BottomModal>
  );
};
export default PatternListModal;

export const NoPatternListModal = ({
  setIsModalOpen,
}: {
  setIsModalOpen: (value: boolean) => void;
}) => {
  const navigate = useNavigate();

  return (
    <BottomModal onClose={() => setIsModalOpen(false)}>
      <Inner>
        <ContentBox>
          <No_Save_Box>
            <No_Save1>저장된 알고리즘이 없어요.</No_Save1>
            <No_Save2>
              3단계로 원하는 조건을 만든 후 빅데이터로 분석하고, 나만의 경매
              성공 공식을 찾아보세요!
            </No_Save2>
          </No_Save_Box>
        </ContentBox>
        <ModalButtonBox>
          <CancelButton
            style={{ width: '114px' }}
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            닫기
          </CancelButton>
          <AnalyzeButton
            style={{ flex: 1 }}
            onClick={() => {
              setIsModalOpen(false);
              navigate('/myAlgorithm');
            }}
          >
            내 알고리즘 만들기
          </AnalyzeButton>
        </ModalButtonBox>
      </Inner>
    </BottomModal>
  );
};

const Title = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const No_Save_Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px 24px 0;
`;

const No_Save1 = styled.span`
  font-family: Spoqa Han Sans Neo;
  font-weight: 700;
  font-size: 18px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222222;
`;

const No_Save2 = styled.span`
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
`;

const Title_Text = styled.span`
  font-family: Spoqa Han Sans Neo;
  font-weight: 700;
  font-size: 18px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #222222;
`;

const Inner = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ViewAllButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  align-self: stretch;
`;

const ViewAllButton = styled.button`
  outline: none;
  border: none;
  background-color: transparent;

  display: flex;
  padding: 0 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const ListBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-top: 1px solid #616161;
`;
const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  border-bottom: 1px solid #e1e2e4;
`;

const CheckBoxContainer = styled.div`
  display: flex;
  padding: 16px 8px;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const PatternTextBox = styled.div`
  display: flex;
  padding: 16px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;
const PatternName = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Grade = styled.div`
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const Patterns = styled.div`
  width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* 2줄까지만 보여줌 */
  overflow: hidden;
  text-overflow: ellipsis;

  font-size: 12px;
  font-weight: 500;
  color: #616161;

  flex-wrap: wrap;
  gap: 4px;

  span:not(:last-child)::after {
    content: ',';
  }
`;

const ModalButtonBox = styled.div`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const CancelButton = styled.button`
  padding-top: 12px;
  padding-right: 28px;
  padding-bottom: 12px;
  padding-left: 28px;
  border-radius: 8px;
  background: #70737c14;
  border: none;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #616161;
  cursor: pointer;
`;

const AnalyzeButton = styled.button`
  border-radius: 8px;
  padding-top: 12px;
  padding-right: 28px;
  padding-bottom: 12px;
  padding-left: 28px;
  background: #008aff;
  border: none;
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #ffffff;
  cursor: pointer;
`;

const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 16px;
  font-weight: 500;
  color: #616161;
`;
